import React from "react";
import { Link } from "react-router-dom";
import { Linkedin, Youtube, Facebook, Instagram, Twitter, Mail, Phone } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { useSettings } from "@/context/SettingsContext";
import { GpuMark } from "@/components/brand/GpuLogo";

const Col = ({ title, links, testid }) => (
  <div data-testid={testid}>
    <h3 className="font-mono text-[10px] uppercase tracking-[0.26em] text-gold">{title}</h3>
    <ul className="mt-5 space-y-3">
      {links.map((l) => (
        <li key={`${l.to}-${l.label}`}>
          <Link
            to={l.to}
            className="text-[13px] text-white/65 transition-colors duration-200 hover:text-white"
          >
            {l.label}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

export const Footer = () => {
  const { t } = useI18n();
  const { settings } = useSettings();
  const social = settings?.social || {};
  const contact = settings?.contact || {};

  const icons = [
    { key: "linkedin", Icon: Linkedin },
    { key: "x", Icon: Twitter },
    { key: "youtube", Icon: Youtube },
    { key: "facebook", Icon: Facebook },
    { key: "instagram", Icon: Instagram },
  ].filter((s) => social[s.key]);

  return (
    <footer className="relative overflow-hidden bg-navy-deep text-white grain" data-testid="site-footer">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 hairline-grid" />
      <div className="relative container pt-20 pb-10 sm:pt-28">
        <div className="grid gap-14 lg:grid-cols-[1.4fr_2.6fr]">
          <div>
            <div className="flex items-center gap-4">
              <GpuMark className="h-16 w-16 sm:h-20 sm:w-20" size="md" onDark />
              <div>
                <p className="font-display text-xl font-bold leading-tight">GUJARAT PHARMA UNION</p>
                <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.26em] text-gold">
                  {t("brand.motto")}
                </p>
              </div>
            </div>
            <p className="mt-7 max-w-md text-[13px] leading-relaxed text-white/55">{t("footer.tagline")}</p>

            <div className="mt-7 space-y-2 text-[13px] text-white/60">
              {contact.email && (
                <a href={`mailto:${contact.email}`} className="flex items-center gap-2 hover:text-white" data-testid="footer-email">
                  <Mail className="h-3.5 w-3.5" /> {contact.email}
                </a>
              )}
              {contact.phone && (
                <a href={`tel:${contact.phone}`} className="flex items-center gap-2 hover:text-white" data-testid="footer-phone">
                  <Phone className="h-3.5 w-3.5" /> {contact.phone}
                </a>
              )}
            </div>

            {icons.length > 0 && (
              <div className="mt-7 flex gap-3">
                {icons.map(({ key, Icon }) => (
                  <a
                    key={key}
                    href={social[key]}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={key}
                    className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-white/70 transition-colors duration-200 hover:border-gold hover:text-gold"
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-10 sm:grid-cols-3 lg:grid-cols-5">
            <Col
              testid="footer-col-organisation"
              title={t("footer.organisation")}
              links={[
                { to: "/about", label: t("nav.aboutGpu") },
                { to: "/objectives", label: t("nav.objectives") },
                { to: "/committees", label: t("nav.committees") },
                { to: "/contact", label: t("nav.contact") },
              ]}
            />
            <Col
              testid="footer-col-membership"
              title={t("footer.membershipCol")}
              links={[
                { to: "/membership", label: t("cta.joinGpu") },
                { to: "/member-benefits", label: t("nav.memberBenefits") },
                { to: "/vendor-registration", label: t("nav.vendorRegistration") },
              ]}
            />
            <Col
              testid="footer-col-protection"
              title={t("footer.protectionCol")}
              links={[
                { to: "/gpu-protection", label: "GPU Shield" },
                { to: "/safe-trade-network", label: t("nav.safeTrade") },
                { to: "/anti-counterfeit", label: t("nav.antiCounterfeit") },
              ]}
            />
            <Col
              testid="footer-col-resources"
              title={t("footer.resourcesCol")}
              links={[
                { to: "/news", label: t("nav.news") },
                { to: "/resources", label: t("footer.circulars") },
                { to: "/downloads", label: t("nav.downloads") },
                { to: "/faq", label: t("nav.faq") },
              ]}
            />
            <Col
              testid="footer-col-legal"
              title={t("footer.legal")}
              links={[
                { to: "/privacy", label: t("footer.privacy") },
                { to: "/terms", label: t("footer.terms") },
                { to: "/disclaimer", label: t("footer.disclaimerPage") },
                { to: "/verify", label: t("nav.verify") },
              ]}
            />
          </div>
        </div>

        <div className="mt-16 border-t border-white/10 pt-7">
          <p className="max-w-3xl text-[11px] leading-relaxed text-white/40">{t("footer.independence")}</p>
          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-white/35">
            {t("footer.rights", { year: new Date().getFullYear() })}
          </p>
        </div>
      </div>
    </footer>
  );
};
